import { faDatabase, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { UseQueryResult } from "@tanstack/react-query";
import { times } from "lodash";
import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";

import { Dataset, DatasetPurpose } from "src/api/types";
import { Button } from "src/base-components/Button";
import { LoadingView } from "src/base-components/LoadingView";
import { Pill } from "src/base-components/Pill";
import { Item } from "src/datasets/DatasetList/Item";
import { ItemSkeleton } from "src/datasets/DatasetList/ItemSkeleton";
import { PendingItem } from "src/datasets/DatasetList/PendingItem";
import { AddDatasetModal } from "src/datasets/DatasetList/addDatasetModal/AddDatasetModal";
import { VersionSchemas } from "src/datasets/DatasetTable/types";
import { PaneHeader } from "src/datasets/PaneHeader";
import { useDatasetJobs, useHideDatasetJob } from "src/datasets/api/queries";
import { useCreateDatasetFromScratch } from "src/datasets/hooks/useCreateDatasetFromScratch";
import { useThereIsDownloadPending } from "src/datasets/hooks/useThereIsDownloadPending";
import { useDatasetFileUpload as useDatasetUpload } from "src/datasets/useDatasetUpload";
import { isDatasetCompatible } from "src/datasets/utils";
import { EmptyState } from "src/design-system/EmptyState";
import { useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import {
  DuplicateDatasetJob,
  AssembleDatasetJob,
  AssembleSimulationDatasetJob,
  SimulationFailuresDatasetJob,
  isAssembleOrDuplicateJob,
} from "src/jobs/types";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { useSimulationAccess } from "src/simulations/useSimulationAccess";

type Props = {
  datasetsQuery: UseQueryResult<Dataset[], Error>;
  schemas: VersionSchemas;
  onAssemble: (type: DatasetPurpose) => void;
  onDelete: (dataset: Dataset) => void;
  onSelect: (dataset: Dataset) => void;
  onRequestClose: () => void;
};

type Filter = "all" | "compatible" | DatasetPurpose;

const renderSkeleton = () => (
  <ul className="flex flex-col gap-y-4">
    {times(3).map((i) => (
      <ItemSkeleton key={i} />
    ))}
  </ul>
);

const filterDatasets = (
  datasets: Dataset[],
  filter: Filter,
  compatibilityDict: Record<string, boolean>,
): Dataset[] => {
  switch (filter) {
    case "compatible":
      return datasets.filter((dataset) =>
        Boolean(compatibilityDict[dataset.id]),
      );
    case "simulation":
    case "test_run":
      return datasets.filter((dataset) => dataset.purpose === filter);
    case "all":
    default:
      return datasets;
  }
};

export const DatasetList: React.FC<Props> = ({
  onAssemble,
  schemas,
  onDelete,
  onSelect,
  onRequestClose,
  datasetsQuery,
}) => {
  const [filter, setFilter] = useLocalStorage<Filter>(
    "dataset_list_filter",
    "all",
  );
  const [_, setSearchParams] = useSearchParams();

  const {
    isOpen: isOpenAddDatasetModal,
    closeModal: closeAddDatasetModal,
    openModal: openAddDatasetModal,
  } = useModal();

  const { workspace, flow, version } = useAuthoringContext();
  const simulationAccess = useSimulationAccess();

  const { uploads, uploadDataset, deleteFromUploads } =
    useDatasetUpload(version);
  const createDatasetFromScratch = useCreateDatasetFromScratch();

  const datasetJobs = useDatasetJobs(workspace.base_url!, flow.id);

  const thereIsADownloadPending = useThereIsDownloadPending();

  const pendingOrFailedDatasetJobs = (datasetJobs.data ?? []).filter(
    (job) => job.status !== "COMPLETED",
  );

  const assembleAndDuplicateDatasetJobs = pendingOrFailedDatasetJobs.filter(
    isAssembleOrDuplicateJob,
  );
  const { mutateAsync: hideDatasetJob } = useHideDatasetJob(
    workspace.base_url!,
    flow.id,
  );

  const handleFilesDropped = async (files: File[], fileErrors: string[]) => {
    if (files.length > 0) {
      toastActions.success({ title: "Dataset uploading..." });
    }

    if (fileErrors.length > 0) {
      toastActions.failure({
        title: "Error uploading dataset files",
        description: fileErrors.join(", "),
      });
    }

    for (const file of files) {
      try {
        const datasetFileUpload = await uploadDataset({
          file,
          purpose: "test_run",
        });
        datasetFileUpload.finishedPromise.then((finishedUpload) => {
          if (finishedUpload.status === "PARTIALLY_COMPLETED") {
            toastActions.warning({
              title: "Dataset processed partially successfully.",
              description: finishedUpload.error_metadata,
              actionText: "View dataset",
              onActionClick: () => {
                setSearchParams({
                  "edit-dataset-id": finishedUpload.dataset_id,
                });
              },
            });
          } else if (finishedUpload.status === "COMPLETED") {
            toastActions.success({ title: "Dataset processed successfully." });
          } else if (finishedUpload.status === "FAILED") {
            toastActions.failure({ title: `Unable to process dataset!` });
          }
        });
      } catch {
        toastActions.failure({ title: "Dataset upload failed!" });
      }
    }
  };
  const AddDatasetButton = () => (
    <Button
      dataLoc="create-dataset-button"
      iconLeft={faPlus}
      size="sm"
      variant="secondary"
      onClick={openAddDatasetModal}
    >
      Create Dataset
    </Button>
  );

  const hasSimulationDatasets = datasetsQuery.data?.some(
    (d: Dataset) => d.purpose === "simulation",
  );
  const hasTestRunDatasets = datasetsQuery.data?.some(
    (d: Dataset) => d.purpose === "test_run",
  );
  const showSimulationAndTestFilters =
    simulationAccess.canAccess && hasSimulationDatasets && hasTestRunDatasets;

  const getPendingDatasetName = (
    job:
      | AssembleDatasetJob
      | AssembleSimulationDatasetJob
      | DuplicateDatasetJob
      | SimulationFailuresDatasetJob,
  ) => {
    if (job.type === "duplicate") {
      return job.request.new_dataset_name ?? "Duplicated dataset";
    }
    return job.request.name;
  };

  // Reset filter to 'all' if the specific filter pills are not shown
  useEffect(() => {
    if (
      !showSimulationAndTestFilters &&
      (filter === "simulation" || filter === "test_run")
    ) {
      setFilter("all");
    }
  }, [showSimulationAndTestFilters, filter, setFilter]);

  return (
    <>
      <PaneHeader onClose={onRequestClose} />
      <div className="flex flex-col gap-y-6 px-6">
        <div className="flex items-center justify-between gap-x-1">
          <div className="space-x-1.5">
            <Pill
              variant={filter === "all" ? "indigo-light" : "outlined-white"}
              onClick={() => setFilter("all")}
            >
              <Pill.Text>All</Pill.Text>
            </Pill>
            <Pill
              variant={
                filter === "compatible" ? "indigo-light" : "outlined-white"
              }
              onClick={() => setFilter("compatible")}
            >
              <Pill.Text>Compatible</Pill.Text>
            </Pill>
            {showSimulationAndTestFilters && (
              <>
                <Pill
                  variant={
                    filter === "simulation" ? "indigo-light" : "outlined-white"
                  }
                  onClick={() => setFilter("simulation")}
                >
                  <Pill.Text>Simulation</Pill.Text>
                </Pill>
                <Pill
                  variant={
                    filter === "test_run" ? "indigo-light" : "outlined-white"
                  }
                  onClick={() => setFilter("test_run")}
                >
                  <Pill.Text>Test</Pill.Text>
                </Pill>
              </>
            )}
          </div>
          <AddDatasetButton />
        </div>
        <LoadingView
          queryResult={datasetsQuery}
          renderUpdated={(datasets: Dataset[]) => {
            const canvasDatasets = datasets.filter(
              (d: Dataset) =>
                !d.purpose ||
                d.purpose === "test_run" ||
                d.purpose === "simulation",
            );

            const compatibilityDict = canvasDatasets.reduce<
              Record<string, boolean>
            >((acc, dataset) => {
              acc[dataset.id] = isDatasetCompatible(dataset, schemas.input);
              return acc;
            }, {});

            const filteredDatasets = filterDatasets(
              canvasDatasets,
              filter,
              compatibilityDict,
            );

            return (
              <ul className="flex flex-col gap-y-4 pb-10">
                {filteredDatasets.length < 1 &&
                  uploads.length < 1 &&
                  assembleAndDuplicateDatasetJobs.length < 1 && (
                    <EmptyState
                      action={<AddDatasetButton />}
                      description="No test dataset was found for this Decision Flow. Click below to add a new test dataset."
                      headline="No test dataset Found"
                      icon={faDatabase}
                    />
                  )}
                {assembleAndDuplicateDatasetJobs.map((job) => (
                  <PendingItem
                    key={job.id}
                    errorMsg={job.error?.message}
                    name={getPendingDatasetName(job)}
                    status={job.status}
                    onDelete={() => hideDatasetJob(job.id)}
                  />
                ))}
                {uploads.map((datasetFileUpload) => (
                  <PendingItem
                    key={datasetFileUpload.id}
                    errorMsg={datasetFileUpload.error_metadata}
                    name={datasetFileUpload.file_name}
                    status={datasetFileUpload.status}
                    onDelete={() => deleteFromUploads(datasetFileUpload.id)}
                  />
                ))}
                {filteredDatasets.map((dataset) => (
                  <Item
                    key={dataset.id}
                    dataset={dataset}
                    disableDownloads={thereIsADownloadPending}
                    incompatible={!compatibilityDict[dataset.id]}
                    onClick={() => onSelect(dataset)}
                    onDelete={() => onDelete(dataset)}
                  />
                ))}
              </ul>
            );
          }}
          renderUpdating={renderSkeleton}
        />
      </div>
      <AddDatasetModal
        open={isOpenAddDatasetModal}
        onAssemble={onAssemble}
        onClose={closeAddDatasetModal}
        onCreate={createDatasetFromScratch}
        onFilesDropped={handleFilesDropped}
      />
    </>
  );
};
